import React, { useState, useEffect } from 'react';
import { NavLink, Navigate, Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import {
  User, Building2, Globe, Settings as SettingsIcon,
  Bell, Mail, MessageCircle, Lock, CreditCard, FileText,
  Palette, Check, Eye, Upload, Loader2, ImageIcon, Trash2, Truck, Zap,
} from 'lucide-react';
import api from '../lib/api';
import { changePasswordSchema, validateForm } from '../lib/validation';
import { useLanguage } from '../contexts/LanguageContext';

import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

// ─────────────────────────────────────────────────────────────
// Nav config
// ─────────────────────────────────────────────────────────────
const getPersonalSections = (t) => [
  { to: 'profile', icon: User, label: t('profileTab') },
  { to: 'language', icon: Globe, label: t('languageTab') },
  { to: 'notifications', icon: Bell, label: t('notificationsTab') },
  { to: 'security', icon: Lock, label: t('securityTab') },
];

const getAdminSections = (t) => [
  { to: 'company', icon: Building2, label: t('companyTab') },
  { to: 'transport', icon: Truck, label: t('transportTab') || 'Transport' },
  { to: 'billing', icon: FileText, label: t('billingTab') },
];

// ─────────────────────────────────────────────────────────────
// Sidebar Nav Item (NavLink)
// ─────────────────────────────────────────────────────────────
const NavItem = ({ icon: Icon, label, to }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-left ${
        isActive
          ? 'bg-background text-foreground shadow-sm border-l-2 border-accent -ml-px'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`
    }
  >
    {({ isActive }) => (
      <>
        <Icon size={18} className={isActive ? 'text-accent' : 'text-muted-foreground'} />
        {label}
      </>
    )}
  </NavLink>
);

// ─────────────────────────────────────────────────────────────
// Mobile Nav Item (NavLink)
// ─────────────────────────────────────────────────────────────
const MobileNavItem = ({ icon: Icon, label, to }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
        isActive
          ? 'bg-muted text-foreground border border-border'
          : 'text-muted-foreground hover:bg-muted'
      }`
    }
  >
    <Icon size={16} />
    {label}
  </NavLink>
);

// ─────────────────────────────────────────────────────────────
// Section: Profil
// ─────────────────────────────────────────────────────────────
const ProfileSection = () => {
  const { user, t } = useOutletContext();
  const { refreshUser } = useAuth();
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const initials = (user?.name || '')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Choisissez une image (PNG, JPEG, WebP ou GIF).'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('Image trop volumineuse (max 2 Mo).'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post('/api/users/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi de la photo.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await api.delete('/api/users/me/avatar');
      await refreshUser();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du retrait de la photo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('profileTab')}</h2>
        <p className="text-sm text-muted-foreground">{t('personalInfo')}</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="h-16 w-16 text-lg">
              {user?.profile_picture && <AvatarImage src={user.profile_picture} alt={user?.name || ''} />}
              <AvatarFallback className="bg-muted text-accent font-semibold">
                {initials || '??'}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <p className="text-base font-semibold text-foreground">{user?.name || '—'}</p>
              <p className="text-sm text-muted-foreground">{user?.email || '—'}</p>
              <div className="flex items-center gap-2 pt-1">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleFile} />
                <Button size="sm" variant="outline" onClick={handlePick} disabled={uploading}>
                  {uploading ? 'Envoi…' : (user?.profile_picture ? 'Changer la photo' : 'Ajouter une photo')}
                </Button>
                {user?.profile_picture && (
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={handleRemove} disabled={uploading}>
                    Retirer
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator className="mb-6" />

          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">{t('name')}</Label>
              <Input value={user?.name || ''} readOnly className="bg-muted" />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('email')}</Label>
              <Input value={user?.email || ''} readOnly className="bg-muted" />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('role')}</Label>
              <Badge variant="secondary" className="capitalize text-sm px-3 py-1">
                {user?.role?.replace(/_/g, ' ') || '—'}
              </Badge>
            </div>
            {user?.assigned_country && (
              <div>
                <Label className="mb-1.5 block">{t('assignedCountry')}</Label>
                <Input value={user.assigned_country} readOnly className="bg-muted" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Langue
// ─────────────────────────────────────────────────────────────
const LanguageSection = () => {
  const { language, changeLanguage, availableLanguages, t } = useOutletContext();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('languageTab')}</h2>
        <p className="text-sm text-muted-foreground">{t('interfaceLanguageDesc')}</p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <Label className="mb-1.5 block font-semibold">{t('interfaceLanguage')}</Label>
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableLanguages.map(l => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.flag} {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-muted border border-border p-3">
            <p className="text-xs text-muted-foreground">
              {t('languageSavedAutomatically')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Notifications
// ─────────────────────────────────────────────────────────────
const NotificationsSection = () => {
  const { notificationPreference, notifLoading, notifMessage, handleNotificationChange, isAdmin, t } = useOutletContext();

  const options = [
    {
      id: 'email',
      icon: Mail,
      label: 'Email',
      description: t('receiveNotifByEmail'),
      activeColor: 'border-accent bg-muted',
      iconBg: 'bg-muted text-accent',
    },
    {
      id: 'whatsapp',
      icon: MessageCircle,
      label: 'WhatsApp',
      description: t('receiveNotifByWhatsApp'),
      activeColor: 'border-accent bg-muted',
      iconBg: 'bg-muted text-accent',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('notificationsTab')}</h2>
        <p className="text-sm text-muted-foreground">{t('preferredNotifChannel')}</p>
      </div>

      {notifMessage && (
        <div className={`rounded-lg border-2 p-3 text-sm font-semibold ${
          notifMessage === t('preferenceSaved')
            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
            : 'bg-red-50 border-red-500 text-red-700'
        }`}>
          {notifMessage}
        </div>
      )}

      <div className="space-y-3">
        {options.map(opt => {
          const isActive = notificationPreference === opt.id;
          return (
            <Card
              key={opt.id}
              onClick={() => !notifLoading && handleNotificationChange(opt.id)}
              className={`cursor-pointer transition-all ${
                isActive ? `border-2 ${opt.activeColor}` : 'hover:border-border'
              } ${notifLoading ? 'pointer-events-none opacity-60' : ''}`}
            >
              <CardContent className="flex items-center gap-4 py-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  isActive ? opt.iconBg : 'bg-muted text-muted-foreground'
                }`}>
                  <opt.icon size={20} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{opt.label}</p>
                  <p className="text-sm text-muted-foreground">{opt.description}</p>
                </div>
                {isActive && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-white">
                    <Check size={14} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isAdmin && <NotificationRulesCard />}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Sécurité (Password)
// ─────────────────────────────────────────────────────────────
const SecuritySection = () => {
  const { t } = useOutletContext();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState('');
  const [pwError, setPwError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMessage('');
    setPwError('');
    setFieldErrors({});

    const validation = validateForm(changePasswordSchema, {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    if (!validation.success) {
      setFieldErrors(validation.errors);
      return;
    }

    setPwLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwMessage(t('passwordChangedSuccess'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwMessage(''), 5000);
    } catch (err) {
      const detail = err.response?.data?.detail || t('passwordChangeError');
      setPwError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setTimeout(() => setPwError(''), 5000);
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('security')}</h2>
        <p className="text-sm text-muted-foreground">{t('changePassword')}</p>
      </div>

      {pwMessage && (
        <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
          {pwMessage}
        </div>
      )}
      {pwError && (
        <div className="rounded-lg border-2 border-red-500 bg-red-50 p-3 text-sm font-semibold text-red-700">
          {pwError}
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label className="mb-1.5 block">{t('currentPassword')}</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className={fieldErrors.currentPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {fieldErrors.currentPassword && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.currentPassword}</p>
              )}
            </div>
            <div>
              <Label className="mb-1.5 block">{t('newPassword')}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className={fieldErrors.newPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {fieldErrors.newPassword && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.newPassword}</p>
              )}
            </div>
            <div>
              <Label className="mb-1.5 block">{t('confirmNewPassword')}</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className={fieldErrors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.confirmPassword}</p>
              )}
            </div>
            <Button type="submit" disabled={pwLoading}>
              {pwLoading ? t('changingPassword') : t('changePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Entreprise (admin)
// ─────────────────────────────────────────────────────────────
const AUDIENCE_LABELS = { sender: 'Expéditeur', recipient: 'Destinataire', both: 'Les deux', none: 'Personne' };

const NotificationRulesCard = () => {
  const [events, setEvents] = useState([]);
  const [audiences, setAudiences] = useState(['sender', 'recipient', 'both', 'none']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/company/notification-rules');
        setEvents(r.data.events || []);
        setAudiences(r.data.audiences || ['sender', 'recipient', 'both', 'none']);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const setChannel = (key, channel, val) =>
    setEvents(evs => evs.map(e => e.key === key ? { ...e, [channel]: val } : e));

  // Défauts intelligents : route toutes les étapes importantes vers le canal
  // préféré (audience = défaut de l'événement), l'autre canal désactivé.
  // 'both' = les deux canaux avec l'audience par défaut. À affiner ensuite si besoin.
  const applyPreset = (mode) =>
    setEvents(evs => evs.map(e => {
      const def = e.default || 'recipient';
      if (mode === 'whatsapp') return { ...e, whatsapp: def, email: 'none' };
      if (mode === 'email') return { ...e, whatsapp: 'none', email: def };
      return { ...e, whatsapp: def, email: def }; // both
    }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const rules = Object.fromEntries(events.map(e => [e.key, { whatsapp: e.whatsapp, email: e.email }]));
      await api.put('/api/company/notification-rules', { rules });
      setSaved(true);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const AudienceSelect = ({ value, onChange }) => (
    <select
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
      className="border border-input rounded-md px-2 py-1 text-sm bg-background w-full"
    >
      {audiences.map(a => <option key={a} value={a}>{AUDIENCE_LABELS[a] || a}</option>)}
    </select>
  );

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div>
          <h3 className="font-semibold text-foreground">Règles de notification</h3>
          <p className="text-sm text-muted-foreground">Pour chaque événement, choisissez qui est notifié, séparément par WhatsApp et par email.</p>
        </div>

        {!loading && (
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-xs font-medium text-foreground mb-2">Canal préféré — défauts intelligents</p>
            <p className="text-xs text-muted-foreground mb-2.5">Appliquez un préréglage à toutes les étapes en un clic, puis affinez si besoin. Enregistrez pour valider.</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('whatsapp')}>
                <MessageCircle className="h-4 w-4 mr-1.5" /> Tout en WhatsApp
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('email')}>
                <Mail className="h-4 w-4 mr-1.5" /> Tout en Email
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('both')}>
                <Check className="h-4 w-4 mr-1.5" /> Les deux
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">Événement</th>
                  <th className="py-2 px-3 font-medium">WhatsApp</th>
                  <th className="py-2 pl-3 font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => (
                  <tr key={e.key} className="border-b border-border last:border-0">
                    <td className="py-2 pr-3">{e.label}</td>
                    <td className="py-2 px-3 min-w-[130px]"><AudienceSelect value={e.whatsapp} onChange={(v) => setChannel(e.key, 'whatsapp', v)} /></td>
                    <td className="py-2 pl-3 min-w-[130px]"><AudienceSelect value={e.email} onChange={(v) => setChannel(e.key, 'email', v)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-3 pt-4">
              <Button onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
              {saved && <span className="text-sm text-green-600">Enregistré ✓</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Transport (mode de chargement)
// ─────────────────────────────────────────────────────────────
const TransportSection = () => {
  const { isAdmin } = useOutletContext();
  const [transport, setTransport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const loadingMode = transport?.loading_mode || 'manual';
  const otpRequired = transport?.delivery_otp_required !== false; // défaut : exigé
  const requireUnload = transport?.require_unload_before_delivery === true; // défaut : non

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/company/settings')
      .then(r => setTransport(r.data?.transport || {}))
      .catch(() => setTransport({}));
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  // Fusionne avec l'objet transport complet (le PUT remplace la clé transport).
  const patchTransport = async (patch) => {
    const merged = { ...(transport || {}), ...patch };
    setTransport(merged);
    setSaving(true);
    setMsg('');
    try {
      await api.put('/api/company/settings', { transport: merged });
      setMsg('Enregistré');
    } catch {
      setMsg('Erreur');
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const save = (mode) => patchTransport({ loading_mode: mode });

  const Opt = ({ val, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => save(val)}
      disabled={saving}
      className={`flex-1 p-4 rounded-lg border-2 text-left transition-all ${loadingMode === val ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-accent" />
        <span className="font-medium text-sm">{title}</span>
        {loadingMode === val && <Check className="h-4 w-4 text-accent ml-auto" />}
      </div>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Transport</h2>
        <p className="text-sm text-muted-foreground">Mode de chargement des colis dans un départ.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Opt val="manual" icon={SettingsIcon} title="Manuel" desc="On assigne les colis au départ, puis on charge." />
            <Opt val="auto" icon={Zap} title="Auto (scan)" desc="Le scan au chargement assigne et charge le colis. Réduit les erreurs." />
          </div>
          {msg && <p className="text-sm text-green-600">{msg}</p>}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-semibold text-foreground">Livraison</h2>
        <p className="text-sm text-muted-foreground">Code de livraison (OTP) exigé pour confirmer la remise au destinataire.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-sm">Code de livraison obligatoire</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Activé : un code à 6 chiffres est envoyé au destinataire et doit être fourni au livreur pour valider la livraison.
                Désactivé : le livreur peut valider sans code.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={otpRequired}
              disabled={saving}
              onClick={() => patchTransport({ delivery_otp_required: !otpRequired })}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${otpRequired ? 'bg-accent' : 'bg-muted-foreground/30'} ${saving ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${otpRequired ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="border-t border-border" />

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-sm">Déchargement obligatoire avant livraison</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Activé : un colis n'est livrable qu'après avoir été déchargé (scan réception à l'arrivée).
                Le déchargement confirme la réception et met en évidence les colis manquants. Désactivé : livraison possible sans déchargement.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={requireUnload}
              disabled={saving}
              onClick={() => patchTransport({ require_unload_before_delivery: !requireUnload })}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${requireUnload ? 'bg-accent' : 'bg-muted-foreground/30'} ${saving ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${requireUnload ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {msg && <p className="text-sm text-green-600">{msg}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

const CompanySection = () => {
  const { isAdmin, t } = useOutletContext();
  const navigate = useNavigate();

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('companyTab')}</h2>
        <p className="text-sm text-muted-foreground">{t('companyGeneralConfig')}</p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('manageCompanyDesc')}
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate('/company-management')}>
            <SettingsIcon size={18} />
            {t('manageCompany')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Section: Facturation (admin)
// ─────────────────────────────────────────────────────────────
const getInvoiceTemplates = (t) => [
  {
    id: 'classic',
    label: t('templateClassic'),
    description: t('templateClassicDesc'),
    preview: { barHeight: 6, barGradient: true, cardBorder: true, cardAccentBar: true },
  },
  {
    id: 'modern',
    label: t('templateModern'),
    description: t('templateModernDesc'),
    preview: { barHeight: 12, barGradient: false, cardBorder: true, cardAccentBar: true, cardRounded: true },
  },
  {
    id: 'minimal',
    label: t('templateMinimal'),
    description: t('templateMinimalDesc'),
    preview: { barHeight: 0, barGradient: false, cardBorder: true, cardAccentBar: false },
  },
  {
    id: 'bold',
    label: t('templateBold'),
    description: t('templateBoldDesc'),
    preview: { barHeight: 0, barGradient: false, cardBorder: true, cardAccentBar: true, fullHeader: true },
  },
];

const TemplateMiniPreview = ({ tpl, accent, isSelected, onClick }) => {
  const p = tpl.preview;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col rounded-lg border-2 p-3 transition-all text-left ${
        isSelected
          ? 'border-accent bg-muted shadow-sm'
          : 'border-border hover:border-muted-foreground/30'
      }`}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-white">
          <Check size={12} />
        </div>
      )}
      {/* Mini mockup */}
      <div className="mb-2 w-full rounded border border-border bg-white overflow-hidden" style={{ height: 80 }}>
        {p.fullHeader ? (
          <div className="h-6 w-full" style={{ background: accent }} />
        ) : p.barHeight > 0 ? (
          <div className="w-full" style={{
            height: p.barHeight > 6 ? 4 : 2,
            background: p.barGradient ? `linear-gradient(135deg, ${accent}, #334155)` : accent,
          }} />
        ) : null}
        <div className="flex gap-1 px-2 pt-2">
          <div className="h-2 w-8 rounded-sm" style={{ background: accent, opacity: 0.7 }} />
          <div className="h-2 w-6 rounded-sm bg-gray-200" />
        </div>
        <div className="flex gap-1 px-2 pt-1">
          {p.cardAccentBar && (
            <div className="h-6 w-1 rounded-sm" style={{ background: accent }} />
          )}
          <div className="flex-1 h-6 rounded border border-gray-100 bg-gray-50" />
          {p.cardAccentBar && (
            <div className="h-6 w-1 rounded-sm" style={{ background: accent }} />
          )}
          <div className="flex-1 h-6 rounded border border-gray-100 bg-gray-50" />
        </div>
        <div className="px-2 pt-1">
          <div className="h-3 w-full rounded bg-gray-100" />
        </div>
      </div>
      <p className="text-sm font-semibold text-foreground">{tpl.label}</p>
      <p className="text-xs text-muted-foreground">{tpl.description}</p>
    </button>
  );
};

const BillingSection = () => {
  const { isAdmin, t } = useOutletContext();
  const [settings, setSettings] = useState({
    invoice_prefix: 'FAC',
    exchange_rates: {},
    invoice_template: 'classic',
    document_mode: 'invoice',
    accent_color: '#0f172a',
    secondary_color: '#1e293b',
    col_label_item: 'Colis',
    col_label_qty: 'Qté',
    col_label_unit_price: 'Prix u. HT',
    col_label_vat: 'TVA (%)',
    col_label_total: 'Total HT',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/invoices/settings')
      .then(r => {
        setSettings(s => ({ ...s, ...r.data }));
        const cacheBust = `?t=${Date.now()}`;
        if (r.data.signature_image_url) setSignaturePreview(r.data.signature_image_url + cacheBust);
        if (r.data.stamp_image_url) setStampPreview(r.data.stamp_image_url + cacheBust);
      })
      .catch(() => {});
    api.get('/api/company/settings')
      .then(r => {
        const url = r.data?.general?.logo_url;
        if (url) setLogoUrl(`${url}?t=${Date.now()}`);
      })
      .catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/invoices/settings', {
        invoice_prefix: settings.invoice_prefix,
        exchange_rates: settings.exchange_rates,
        default_client_type: settings.default_client_type || 'sender',
        invoice_template: settings.invoice_template,
        document_mode: settings.document_mode || 'invoice',
        accent_color: settings.accent_color,
        secondary_color: settings.secondary_color,
        col_label_item: settings.col_label_item,
        col_label_qty: settings.col_label_qty,
        col_label_unit_price: settings.col_label_unit_price,
        col_label_vat: settings.col_label_vat,
        col_label_total: settings.col_label_total,
      });
      setMsg(t('settingsSaved'));
    } catch {
      setMsg(t('saveError'));
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 4000);
  };

  const uploadImage = async (file, type) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const resp = await api.post(`/api/invoices/settings/${type}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = type === 'signature' ? resp.data.signature_image_url : resp.data.stamp_image_url;
      const preview = url ? `${url}?t=${Date.now()}` : URL.createObjectURL(file);
      if (type === 'signature') setSignaturePreview(preview);
      else setStampPreview(preview);
      setMsg(t(type === 'signature' ? 'signatureUploaded' : 'stampUploaded'));
    } catch {
      setMsg(t('uploadError'));
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const deleteImage = async (type) => {
    try {
      await api.delete(`/api/invoices/settings/${type}`);
      if (type === 'signature') setSignaturePreview(null);
      else setStampPreview(null);
      setMsg(t(type === 'signature' ? 'signatureDeleted' : 'stampDeleted') || 'Supprimé');
    } catch {
      setMsg(t('saveError') || 'Erreur');
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMsg(t('invalidImageType') || 'Type de fichier non autorisé');
      setTimeout(() => setMsg(''), 4000);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMsg(t('fileTooLarge') || 'Fichier trop volumineux (max 2 Mo)');
      setTimeout(() => setMsg(''), 4000);
      return;
    }
    try {
      setUploadingLogo(true);
      const form = new FormData();
      form.append('file', file);
      const resp = await api.post('/api/company/settings/upload-logo', form);
      setLogoUrl(`${resp.data.logo_url}?t=${Date.now()}`);
      setMsg(t('logoUploadSuccess') || 'Logo mis à jour');
    } catch {
      setMsg(t('uploadError'));
    }
    setUploadingLogo(false);
    e.target.value = '';
    setTimeout(() => setMsg(''), 4000);
  };

  const handleLogoDelete = async () => {
    try {
      await api.put('/api/company/settings', { general: { logo_url: '' } });
      setLogoUrl('');
      setMsg(t('logoDeleted') || 'Logo supprimé');
    } catch {
      setMsg(t('saveError'));
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const resp = await api.get('/api/invoices/preview', {
        params: {
          template: settings.invoice_template,
          accent_color: settings.accent_color,
          secondary_color: settings.secondary_color,
          document_mode: settings.document_mode || 'invoice',
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch {
      setMsg(t('previewLoadError'));
      setTimeout(() => setMsg(''), 4000);
    }
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const isValidHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v);

  const currencies = [
    { code: 'XAF', label: t('currencyXAF') },
    { code: 'USD', label: t('currencyUSD') },
    { code: 'GBP', label: t('currencyGBP') },
    { code: 'CAD', label: t('currencyCAD') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('billingTab')}</h2>
        <p className="text-sm text-muted-foreground">{t('billingDesc')}</p>
      </div>

      {msg && (
        <div className={`rounded-lg border-2 p-3 text-sm font-semibold ${
          (msg === t('saveError') || msg === t('uploadError') || msg === t('previewLoadError'))
            ? 'bg-red-50 border-red-500 text-red-700'
            : 'bg-emerald-50 border-emerald-500 text-emerald-700'
        }`}>
          {msg}
        </div>
      )}

      {/* Modele de facture */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
              <Palette size={18} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{t('invoiceTemplate')}</p>
              <p className="text-xs text-muted-foreground">{t('invoiceTemplateDesc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {getInvoiceTemplates(t).map(tpl => (
              <TemplateMiniPreview
                key={tpl.id}
                tpl={tpl}
                accent={settings.accent_color}
                isSelected={settings.invoice_template === tpl.id}
                onClick={() => setSettings(s => ({ ...s, invoice_template: tpl.id }))}
              />
            ))}
          </div>

          <Separator />

          {/* Type de document généré à la création */}
          <div>
            <Label className="mb-1.5 block font-semibold">Document à la création du colis</Label>
            <p className="text-xs text-muted-foreground mb-2">Choisissez ce qui est généré et envoyé au client.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              {[
                ['invoice', 'Facture', 'Document avec montants, TVA et paiement.'],
                ['receipt', 'Reçu de dépôt', 'Accusé de réception des colis, sans montants.'],
              ].map(([val, title, desc]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, document_mode: val }))}
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${(settings.document_mode || 'invoice') === val ? 'border-accent bg-accent/5' : 'border-border hover:border-muted-foreground/30'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{title}</span>
                    {(settings.document_mode || 'invoice') === val && <Check className="h-4 w-4 text-accent ml-auto" />}
                  </div>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block font-semibold">{t('primaryColor')}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.accent_color}
                  onChange={e => setSettings(s => ({ ...s, accent_color: e.target.value }))}
                  className="h-9 w-9 cursor-pointer rounded border border-border p-0.5"
                />
                <Input
                  value={settings.accent_color}
                  onChange={e => {
                    const v = e.target.value;
                    setSettings(s => ({ ...s, accent_color: v }));
                  }}
                  className={`w-28 font-mono text-sm ${!isValidHex(settings.accent_color) ? 'border-red-500' : ''}`}
                  maxLength={7}
                />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block font-semibold">{t('secondaryColor')}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.secondary_color}
                  onChange={e => setSettings(s => ({ ...s, secondary_color: e.target.value }))}
                  className="h-9 w-9 cursor-pointer rounded border border-border p-0.5"
                />
                <Input
                  value={settings.secondary_color}
                  onChange={e => {
                    const v = e.target.value;
                    setSettings(s => ({ ...s, secondary_color: v }));
                  }}
                  className={`w-28 font-mono text-sm ${!isValidHex(settings.secondary_color) ? 'border-red-500' : ''}`}
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <Label className="mb-1.5 block font-semibold">Libellés des colonnes du tableau</Label>
            <p className="text-xs text-muted-foreground mb-3">Personnalisez les en-têtes du tableau des lignes de la facture.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ['col_label_item', 'Désignation', 'Colis'],
                ['col_label_qty', 'Quantité', 'Qté'],
                ['col_label_unit_price', 'Prix unitaire', 'Prix u. HT'],
                ['col_label_vat', 'TVA', 'TVA (%)'],
                ['col_label_total', 'Total', 'Total HT'],
              ].map(([key, label, placeholder]) => (
                <div key={key}>
                  <Label className="mb-1 block text-xs text-muted-foreground">{label}</Label>
                  <Input
                    value={settings[key] ?? ''}
                    placeholder={placeholder}
                    onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
                    maxLength={40}
                    className="text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? t('saving') : t('save')}
            </Button>
            <Button variant="outline" onClick={openPreview} disabled={previewLoading}>
              <Eye size={16} className="mr-1.5" />
              {previewLoading ? t('loading') : t('preview')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{t('invoicePreview')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-6 pb-6">
            {previewUrl && (
              <iframe
                src={previewUrl}
                title={t('invoicePreview')}
                className="h-full w-full rounded-lg border border-border"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Numerotation & client */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
              <FileText size={18} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{t('numbering')}</p>
              <p className="text-xs text-muted-foreground">{t('numberingDesc')}</p>
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block font-semibold">{t('invoicePrefix')}</Label>
            <Input
              value={settings.invoice_prefix || 'FAC'}
              onChange={e => setSettings(s => ({ ...s, invoice_prefix: e.target.value }))}
              placeholder="FAC"
              className="w-32"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('invoicePrefixExample')}</p>
          </div>

          <div>
            <Label className="mb-1.5 block font-semibold">{t('defaultClient')}</Label>
            <Select
              value={settings.default_client_type || 'sender'}
              onValueChange={val => setSettings(s => ({ ...s, default_client_type: val }))}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sender">{t('sender')}</SelectItem>
                <SelectItem value="recipient">{t('recipient')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">{t('defaultClientDesc')}</p>
          </div>

          <Button onClick={saveSettings} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </CardContent>
      </Card>

      {/* Taux de change */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
              <CreditCard size={18} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{t('exchangeRates')}</p>
              <p className="text-xs text-muted-foreground">{t('exchangeRatesDesc')}</p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">{t('leaveEmptyNoConversion')}</p>

          <div className="space-y-3">
            {currencies.map(c => (
              <div key={c.code} className="flex items-center gap-3">
                <span className="w-44 text-sm font-medium text-foreground">{c.label}</span>
                <Input
                  type="number"
                  placeholder="ex: 655"
                  value={settings.exchange_rates?.[c.code] || ''}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    setSettings(s => ({
                      ...s,
                      exchange_rates: { ...s.exchange_rates, [c.code]: isNaN(val) ? undefined : val },
                    }));
                  }}
                  className="w-32"
                />
              </div>
            ))}
          </div>

          <Button onClick={saveSettings} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </CardContent>
      </Card>

      {/* Logo entreprise */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
              <ImageIcon size={18} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{t('companyLogo') || 'Logo entreprise'}</p>
              <p className="text-xs text-muted-foreground">{t('companyLogoDesc') || 'Apparaît sur les factures et les e-mails envoyés aux clients'}</p>
            </div>
          </div>

          {logoUrl ? (
            <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
              <img
                src={logoUrl}
                alt="Logo"
                className="h-16 w-16 object-contain rounded border bg-white p-1"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingLogo}
                  onClick={() => logoInputRef.current?.click()}
                  className="flex items-center gap-2"
                >
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {t('modify') || 'Modifier'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleLogoDelete}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  {t('delete') || 'Supprimer'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-28 border-2 border-dashed rounded-lg bg-muted/30 gap-3">
              <span className="text-xs text-muted-foreground">{t('noLogo') || 'Aucun logo configuré'}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingLogo}
                onClick={() => logoInputRef.current?.click()}
                className="flex items-center gap-2"
              >
                {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {t('uploadImage') || 'Importer une image'}
              </Button>
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t('logoUploadHint') || 'PNG, JPG, WebP ou SVG — max 2 Mo'}</p>

          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoUpload}
          />
        </CardContent>
      </Card>

      {/* Signature & Cachet */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
              <Palette size={18} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{t('signatureAndStamp')}</p>
              <p className="text-xs text-muted-foreground">{t('signatureAndStampDesc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Signature */}
            <div className="space-y-3">
              <Label className="font-semibold">{t('signatureLabel')}</Label>
              {signaturePreview ? (
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <img
                    src={signaturePreview}
                    alt="Signature"
                    className="h-16 object-contain rounded border bg-white"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-20 border-2 border-dashed rounded-lg bg-muted/30">
                  <span className="text-xs text-muted-foreground">{t('noSignature') || 'Aucune signature'}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('billing-signature-input').click()}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {signaturePreview ? (t('modify') || 'Modifier') : (t('uploadImage') || 'Importer une image')}
                </Button>
                {signaturePreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteImage('signature')}
                  >
                    {t('delete') || 'Supprimer'}
                  </Button>
                )}
              </div>
              <input
                id="billing-signature-input"
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={e => {
                  e.target.files?.[0] && uploadImage(e.target.files[0], 'signature');
                  e.target.value = '';
                }}
              />
            </div>

            {/* Cachet */}
            <div className="space-y-3">
              <Label className="font-semibold">{t('stampLabel')}</Label>
              {stampPreview ? (
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <img
                    src={stampPreview}
                    alt="Cachet"
                    className="h-16 object-contain rounded border bg-white"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-20 border-2 border-dashed rounded-lg bg-muted/30">
                  <span className="text-xs text-muted-foreground">{t('noStamp') || 'Aucun cachet'}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('billing-stamp-input').click()}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {stampPreview ? (t('modify') || 'Modifier') : (t('uploadImage') || 'Importer une image')}
                </Button>
                {stampPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteImage('stamp')}
                  >
                    {t('delete') || 'Supprimer'}
                  </Button>
                )}
              </div>
              <input
                id="billing-stamp-input"
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={e => {
                  e.target.files?.[0] && uploadImage(e.target.files[0], 'stamp');
                  e.target.value = '';
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Settings Layout (parent route component)
// ─────────────────────────────────────────────────────────────
const SettingsLayout = () => {
  const { user } = useAuth();
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  const isAdmin = ['super_admin', 'company_admin', 'country_admin'].includes(user?.role);

  const [notificationPreference, setNotificationPreference] = useState('email');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');

  useEffect(() => {
    api.get('/api/users/me')
      .then(r => {
        if (r.data.notification_preference) {
          setNotificationPreference(r.data.notification_preference);
        }
      })
      .catch(err => console.error('Error fetching preference:', err));
  }, []);

  const handleNotificationChange = async (preference) => {
    try {
      setNotifLoading(true);
      await api.put('/api/users/me/notification-preference', { notification_preference: preference });
      setNotificationPreference(preference);
      setNotifMessage(t('preferenceSaved'));
      setTimeout(() => setNotifMessage(''), 3000);
    } catch (err) {
      console.error('Error updating preference:', err);
      setNotifMessage(t('preferenceUpdateError'));
      setTimeout(() => setNotifMessage(''), 3000);
    } finally {
      setNotifLoading(false);
    }
  };

  return (
    <ProfessionalLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t('settings')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('managePreferences')}
        </p>
      </div>

      {/* Mobile nav — horizontal scrollable */}
      <div className="mb-4 md:hidden">
        <ScrollArea className="w-full">
          <div className="flex gap-2 pb-2">
            {getPersonalSections(t).map(s => (
              <MobileNavItem key={s.to} icon={s.icon} label={s.label} to={s.to} />
            ))}
            {isAdmin && (
              <>
                <Separator orientation="vertical" className="mx-1 h-8 self-center" />
                {getAdminSections(t).map(s => (
                  <MobileNavItem key={s.to} icon={s.icon} label={s.label} to={s.to} />
                ))}
              </>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Desktop: sidebar + content */}
      <div className="flex gap-6">
        {/* Sidebar nav — desktop only */}
        <aside className="hidden md:block w-64 shrink-0">
          <nav className="sticky top-6 rounded-xl border border-border bg-muted p-3 space-y-1">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('personal')}
            </p>
            {getPersonalSections(t).map(s => (
              <NavItem key={s.to} icon={s.icon} label={s.label} to={s.to} />
            ))}

            {isAdmin && (
              <>
                <Separator className="my-3" />
                <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('administration')}
                </p>
                {getAdminSections(t).map(s => (
                  <NavItem key={s.to} icon={s.icon} label={s.label} to={s.to} />
                ))}
              </>
            )}
          </nav>
        </aside>

        {/* Content panel */}
        <main className="min-w-0 flex-1">
          <Outlet context={{
            user, t, isAdmin,
            language, changeLanguage, availableLanguages,
            notificationPreference, notifLoading, notifMessage, handleNotificationChange,
          }} />
        </main>
      </div>
    </ProfessionalLayout>
  );
};

export { ProfileSection, LanguageSection, NotificationsSection, SecuritySection, CompanySection, TransportSection, BillingSection };
export default SettingsLayout;
